import React, { useState, useEffect, useRef, useCallback } from 'react';
import { threadsAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Send, MoreVertical, CheckCircle2, ListTodo, 
  ChevronDown, ChevronUp, Reply, Archive,
  Lock, Building2, Briefcase, FolderKanban, Megaphone, User,
  Video, Phone, Users as UsersIcon, Sparkles
} from 'lucide-react';
import PremiumVideoCall from '../../components/video/PremiumVideoCall';
import { DESIGN } from '../../components/DesignSystem';

// Thread type configurations for header gradient
const TYPE_GRADIENTS = {
  private: `linear-gradient(135deg, #A855F7 0%, #7C3AED 100%)`,
  department: `linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)`,
  role: `linear-gradient(135deg, #F59E0B 0%, #D97706 100%)`,
  project: `linear-gradient(135deg, #10B981 0%, #059669 100%)`,
  system: `linear-gradient(135deg, #EF4444 0%, #DC2626 100%)`,
  personal: `linear-gradient(135deg, #64748B 0%, #475569 100%)`
};

const TYPE_ICONS = {
  private: Lock,
  department: Building2,
  role: Briefcase,
  project: FolderKanban,
  system: Megaphone,
  personal: User
};

// Speaker colors
const SPEAKER_COLORS = [
  '#A855F7', '#3B82F6', '#10B981', '#F59E0B', '#EC4899', 
  '#6366F1', '#14B8A6', '#F97316', '#EF4444', '#8B5CF6'
];

const getSpeakerColor = (name) => {
  const hash = name?.split('').reduce((a, b) => a + b.charCodeAt(0), 0) || 0;
  return SPEAKER_COLORS[hash % SPEAKER_COLORS.length];
};

const ThreadConversation = ({ thread, currentUser, canPost, onMessageSent, onThreadUpdate }) => {
  const [messages, setMessages] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [showDecisions, setShowDecisions] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (thread?.id) {
      checkActiveCall();
    }
  }, [thread?.id]);

  const checkActiveCall = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/video/rooms/thread/${thread.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.active) {
        setActiveCall(data.room);
      } else {
        setActiveCall(null);
      }
    } catch (error) {
      console.error('Error checking active call:', error);
    }
  };

  const startVideoCall = () => setShowVideoCall(true);
  const joinVideoCall = () => setShowVideoCall(true);

  const fetchMessages = useCallback(async () => {
    if (!thread?.id) return;
    
    try {
      setLoading(true);
      const [msgRes, decRes] = await Promise.all([
        threadsAPI.getMessages(thread.id),
        threadsAPI.getDecisions(thread.id)
      ]);
      setMessages(msgRes.data.messages || []);
      setDecisions(decRes.data.decisions || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [thread?.id]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);
      const messageData = {
        content: newMessage.trim(),
        replyToId: replyTo?.id || null,
        attachments: [],
        mentions: []
      };

      const response = await threadsAPI.sendMessage(thread.id, messageData);
      setMessages(prev => [...prev, response.data.message]);
      setNewMessage('');
      setReplyTo(null);
      onMessageSent?.();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleToggleDecision = async (message) => {
    try {
      const newIsDecision = !message.isDecision;
      await threadsAPI.markAsDecision(message.id, newIsDecision);
      
      setMessages(prev => prev.map(m => 
        m.id === message.id ? { ...m, isDecision: newIsDecision } : m
      ));
      
      const decRes = await threadsAPI.getDecisions(thread.id);
      setDecisions(decRes.data.decisions || []);
      
      toast.success(newIsDecision ? 'Marked as decision' : 'Removed decision');
    } catch (error) {
      console.error('Error toggling decision:', error);
      toast.error('Failed to update decision');
    }
  };

  const formatTime = (timestamp) => {
    try { return format(new Date(timestamp), 'h:mm a'); } 
    catch { return ''; }
  };

  const formatDate = (timestamp) => {
    try { return format(new Date(timestamp), 'MMM d, yyyy'); } 
    catch { return ''; }
  };

  const canManageDecisions = currentUser?.id === thread?.createdBy || 
    currentUser?.role === 'admin' || currentUser?.role === 'owner';

  const TypeIcon = TYPE_ICONS[thread?.type] || Lock;

  return (
    <div className="flex flex-col h-full" data-testid="thread-conversation">
      {/* Premium Header with rich gradient */}
      <div 
        className="p-5 flex-shrink-0 relative overflow-hidden"
        style={{ background: TYPE_GRADIENTS[thread?.type] || TYPE_GRADIENTS.private }}
      >
        {/* Subtle gradient overlay */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{ 
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)'
          }}
        />
        
        <div className="relative flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <TypeIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-lg text-white">{thread?.title}</h2>
              {thread?.description && (
                <p className="text-sm text-white/80 mt-0.5">{thread.description}</p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Video Call Button */}
            {activeCall ? (
              <Button
                size="sm"
                onClick={joinVideoCall}
                className="bg-green-500 hover:bg-green-600 text-white gap-2 rounded-xl"
                data-testid="join-call-btn"
              >
                <Phone className="w-4 h-4 animate-pulse" />
                Join ({activeCall.participant_count})
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={startVideoCall}
                className="text-white hover:bg-white/20 gap-2 rounded-xl backdrop-blur-sm"
                data-testid="start-call-btn"
              >
                <Video className="w-4 h-4" />
                Video Call
              </Button>
            )}
            
            {/* Decisions Button */}
            {decisions.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDecisions(!showDecisions)}
                className="text-white hover:bg-white/20 gap-2 rounded-xl backdrop-blur-sm"
              >
                <CheckCircle2 className="w-4 h-4" />
                Decisions ({decisions.length})
                {showDecisions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            )}
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 h-9 w-9 p-0 rounded-xl">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem>
                  <Archive className="w-4 h-4 mr-2" />
                  Archive Thread
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Premium Decisions Panel */}
      {showDecisions && decisions.length > 0 && (
        <div 
          className="p-5 border-b animate-in slide-in-from-top duration-200"
          style={{ 
            background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.15) 0%, rgba(234, 179, 8, 0.05) 100%)',
            borderColor: DESIGN.colors.amber
          }}
        >
          <h3 className="font-bold text-sm mb-3 flex items-center gap-2" style={{ color: '#92400E' }}>
            <CheckCircle2 className="w-5 h-5" />
            Decisions
          </h3>
          <div className="space-y-3">
            {decisions.map(decision => (
              <div 
                key={decision.id}
                className="p-4 rounded-xl backdrop-blur-sm"
                style={{ 
                  backgroundColor: 'rgba(255,255,255,0.8)',
                  boxShadow: DESIGN.shadows.sm
                }}
              >
                <p className="text-sm font-medium line-clamp-2" style={{ color: '#78350F' }}>
                  {decision.content}
                </p>
                <p className="text-xs mt-2" style={{ color: '#92400E' }}>
                  by {decision.markedByName} • {formatDate(decision.timestamp)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Messages Area */}
      <ScrollArea className="flex-1 p-6">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div 
              className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: DESIGN.colors.primary, borderTopColor: 'transparent' }}
            />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
              style={{ backgroundColor: DESIGN.colors.primarySubtle }}
            >
              <Sparkles className="w-8 h-8" style={{ color: DESIGN.colors.primary }} />
            </div>
            <p className="font-semibold" style={{ color: DESIGN.colors.textSecondary }}>No messages yet</p>
            <p className="text-sm mt-1" style={{ color: DESIGN.colors.textMuted }}>Start the conversation</p>
          </div>
        ) : (
          <div className="space-y-5">
            {messages.map((message, index) => {
              const isOwnMessage = message.senderId === currentUser?.id;
              const showDate = index === 0 || 
                formatDate(message.timestamp) !== formatDate(messages[index - 1]?.timestamp);
              const speakerColor = getSpeakerColor(message.senderName);
              
              return (
                <React.Fragment key={message.id}>
                  {/* Premium Date Separator */}
                  {showDate && (
                    <div className="flex items-center gap-4 my-6">
                      <div className="flex-1 h-px" style={{ backgroundColor: DESIGN.colors.border }} />
                      <span 
                        className="text-xs px-4 py-1.5 rounded-full font-semibold"
                        style={{ backgroundColor: DESIGN.colors.background, color: DESIGN.colors.textMuted }}
                      >
                        {formatDate(message.timestamp)}
                      </span>
                      <div className="flex-1 h-px" style={{ backgroundColor: DESIGN.colors.border }} />
                    </div>
                  )}
                  
                  {/* Message */}
                  <div 
                    className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} animate-in fade-in duration-200`}
                    data-testid={`message-${message.id}`}
                  >
                    <div className={`max-w-[70%] ${isOwnMessage ? 'items-end' : 'items-start'} flex flex-col`}>
                      {/* Sender Avatar & Name */}
                      {!isOwnMessage && (
                        <div className="flex items-center gap-2 mb-2 px-1">
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
                            style={{ backgroundColor: speakerColor }}
                          >
                            {message.senderAvatar}
                          </div>
                          <span className="text-sm font-semibold" style={{ color: DESIGN.colors.textPrimary }}>
                            {message.senderName}
                          </span>
                        </div>
                      )}
                      
                      {/* Reply Preview */}
                      {message.replyToPreview && (
                        <div 
                          className="text-xs px-3 py-2 rounded-t-xl mb-0.5 border-l-3"
                          style={{ 
                            backgroundColor: DESIGN.colors.background, 
                            color: DESIGN.colors.textMuted,
                            borderLeftColor: DESIGN.colors.border
                          }}
                        >
                          {message.replyToPreview}
                        </div>
                      )}
                      
                      {/* Message Bubble */}
                      <div 
                        className={`relative group px-5 py-3.5 rounded-2xl shadow-sm ${
                          isOwnMessage ? 'rounded-br-md' : 'rounded-bl-md'
                        }`}
                        style={{ 
                          background: isOwnMessage 
                            ? `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`
                            : DESIGN.colors.card,
                          color: isOwnMessage ? '#FFFFFF' : DESIGN.colors.textPrimary,
                          boxShadow: isOwnMessage 
                            ? `0 4px 15px ${DESIGN.colors.primary}30`
                            : DESIGN.shadows.sm
                        }}
                      >
                        {/* Decision Badge - Premium golden amber */}
                        {message.isDecision && (
                          <div 
                            className="absolute -top-3 -left-2 flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold"
                            style={{ 
                              background: 'linear-gradient(135deg, #EAB308 0%, #CA8A04 100%)',
                              color: '#422006',
                              boxShadow: '0 2px 10px rgba(234, 179, 8, 0.4)'
                            }}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Decision
                          </div>
                        )}
                        
                        {/* Task Badge */}
                        {message.linkedTaskId && (
                          <Badge 
                            className="absolute -top-2 -right-2 text-xs px-2 py-0.5"
                            style={{ backgroundColor: DESIGN.colors.blueSubtle, color: DESIGN.colors.blue }}
                          >
                            <ListTodo className="w-3 h-3 mr-1" />
                            Task
                          </Badge>
                        )}
                        
                        <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</p>
                        
                        {/* Message Actions */}
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-7 w-7 p-0 rounded-lg"
                                style={{ 
                                  backgroundColor: isOwnMessage ? 'rgba(255,255,255,0.2)' : DESIGN.colors.background,
                                  color: isOwnMessage ? '#FFFFFF' : DESIGN.colors.textMuted
                                }}
                              >
                                <MoreVertical className="w-3.5 h-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setReplyTo(message)}>
                                <Reply className="w-4 h-4 mr-2" />
                                Reply
                              </DropdownMenuItem>
                              {canManageDecisions && (
                                <DropdownMenuItem onClick={() => handleToggleDecision(message)}>
                                  <CheckCircle2 className="w-4 h-4 mr-2" />
                                  {message.isDecision ? 'Remove Decision' : 'Mark as Decision'}
                                </DropdownMenuItem>
                              )}
                              {canManageDecisions && !message.linkedTaskId && (
                                <DropdownMenuItem>
                                  <ListTodo className="w-4 h-4 mr-2" />
                                  Convert to Task
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                      
                      {/* Timestamp */}
                      <span 
                        className={`text-xs mt-1.5 px-1 font-medium ${isOwnMessage ? 'text-right' : 'text-left'}`}
                        style={{ color: DESIGN.colors.textMuted }}
                      >
                        {formatTime(message.timestamp)}
                      </span>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </ScrollArea>

      {/* Reply Preview */}
      {replyTo && (
        <div 
          className="px-5 py-3 flex items-center justify-between animate-in slide-in-from-bottom duration-200"
          style={{ backgroundColor: DESIGN.colors.background, borderTop: `1px solid ${DESIGN.colors.border}` }}
        >
          <div className="flex items-center gap-3">
            <Reply className="w-4 h-4" style={{ color: DESIGN.colors.textMuted }} />
            <span className="text-sm" style={{ color: DESIGN.colors.textMuted }}>
              Replying to <span className="font-semibold">{replyTo.senderName}</span>
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setReplyTo(null)} className="h-7 w-7 p-0">
            ×
          </Button>
        </div>
      )}

      {/* Premium Message Input */}
      {canPost ? (
        <form onSubmit={handleSendMessage} className="p-5" style={{ borderTop: `1px solid ${DESIGN.colors.border}` }}>
          <div 
            className="flex items-center gap-3 p-3 rounded-2xl transition-all focus-within:shadow-lg"
            style={{ 
              backgroundColor: DESIGN.colors.background,
              border: `1px solid ${DESIGN.colors.border}`,
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'
            }}
          >
            <Input
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
              disabled={sending}
              data-testid="message-input"
            />
            <button 
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="w-11 h-11 rounded-xl flex items-center justify-center transition-all disabled:opacity-50"
              style={{ 
                background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                boxShadow: newMessage.trim() ? `0 0 15px ${DESIGN.colors.primary}40` : 'none'
              }}
              data-testid="send-message-btn"
            >
              <Send className="w-5 h-5 text-white" />
            </button>
          </div>
        </form>
      ) : (
        <div 
          className="p-5 text-center text-sm"
          style={{ backgroundColor: DESIGN.colors.background, color: DESIGN.colors.textMuted, borderTop: `1px solid ${DESIGN.colors.border}` }}
        >
          Only admins can post in this thread
        </div>
      )}
      
      {/* Premium Video Call Component */}
      {showVideoCall && (
        <PremiumVideoCall
          threadId={thread?.id}
          currentUser={currentUser}
          onClose={() => {
            setShowVideoCall(false);
            checkActiveCall();
          }}
          onPostSummary={async (summary) => {
            try {
              const summaryContent = `📹 **Video Call Summary**\n\n**Duration:** ${summary.duration}\n**Participants:** ${summary.participants?.join(', ')}\n\n**Key Points:**\n${summary.keyPoints?.map(p => `• ${p}`).join('\n')}\n\n**Decisions:**\n${summary.decisions?.map(d => `✓ ${d}`).join('\n')}\n\n**Action Items:**\n${summary.actionItems?.map(a => `☐ ${a.task}${a.assignee ? ` (@${a.assignee})` : ''}`).join('\n')}`;
              
              await threadsAPI.postMessage(thread.id, { content: summaryContent });
              toast.success('Meeting summary posted to thread');
            } catch (error) {
              console.error('Failed to post summary:', error);
              toast.error('Failed to post meeting summary');
            }
          }}
          initialRoomId={activeCall?.room_id}
        />
      )}
    </div>
  );
};

export default ThreadConversation;
